export type EditablePartOfUiConfig = Omit<
  Pick<UiConfig, 'display_name' | 'props' | 'children_ids' | 'dataset_instructions' | 'alias'>,
  'display_name' | 'alias' | 'dataset_instructions'
> & {
  display_name?: string | null
  alias?: UiConfigAliases
  dataset_instructions?: Partial<DatasetInstruction>[]
}

export type SaveReadyEditablePartOfUiConfig = Omit<
  EditablePartOfUiConfig,
  'display_name' | 'alias' | 'dataset_instructions'
> & {
  display_name?: string
  alias: UiConfigAliases
  dataset_instructions: Partial<DatasetInstruction>[]
}

export type EditableUiConfigValidationResult =
  | {
      isValid: true
      validatedEditableUiConfig: EditablePartOfUiConfig
    }
  | {
      isValid: false
      validationError: string
    }

function getEditablePartOfUiConfigValidationError(value: unknown): string | undefined {
  if (!isObjectRecord(value)) {
    return 'JSON must be an object.'
  }

  if (!isObjectRecord(value.props)) {
    return 'Field "props" must be an object.'
  }

  if (value.dataset_instructions !== undefined && !isDatasetInstructions(value.dataset_instructions)) {
    return 'Field "dataset_instructions" must be an array of objects.'
  }

  if (value.alias !== undefined && !isUiConfigAliases(value.alias)) {
    return 'Field "alias" must be an object with string, number, null, or arrays of those values.'
  }

  if (value.display_name !== undefined && value.display_name !== null && typeof value.display_name !== 'string') {
    return 'Field "display_name" must be a string, null, or omitted.'
  }

  if (value.children_ids !== undefined && !isChildrenIds(value.children_ids)) {
    return 'Field "children_ids" must be an array of numbers.'
  }

  return undefined
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isChildrenIds(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'number')
}

function isDatasetInstructions(value: unknown): value is Partial<DatasetInstruction>[] {
  return Array.isArray(value) && value.every((entry) => isObjectRecord(entry))
}

function isUiConfigAliasPrimitive(value: unknown): value is UiConfigAliasPrimitive {
  return value === null || typeof value === 'string' || typeof value === 'number'
}

function isUiConfigAliasValue(value: unknown): value is UiConfigAliasValue {
  if (isUiConfigAliasPrimitive(value)) {
    return true
  }

  return Array.isArray(value) && value.every((entry) => isUiConfigAliasPrimitive(entry))
}

function isUiConfigAliases(value: unknown): value is UiConfigAliases {
  if (!isObjectRecord(value)) {
    return false
  }

  return Object.values(value).every((entry) => isUiConfigAliasValue(entry))
}

export function validateEditableUiConfigJson(jsonValue: string): EditableUiConfigValidationResult {
  try {
    const parsedValue: unknown = JSON.parse(jsonValue)
    const validationError = getEditablePartOfUiConfigValidationError(parsedValue)

    if (validationError) {
      return {
        isValid: false,
        validationError,
      }
    }

    return {
      isValid: true,
      validatedEditableUiConfig: parsedValue as EditablePartOfUiConfig,
    }
  } catch (_error: unknown) {
    return {
      isValid: false,
      validationError: 'JSON syntax is invalid.',
    }
  }
}

export function fillValidatedEditableUiConfigDefaults(
  editableUiConfig: EditablePartOfUiConfig
): SaveReadyEditablePartOfUiConfig {
  // Clone first so the caller's object is never mutated.
  // Mutate the clone in-place so that the authored key order is preserved.
  // Spread-based reconstruction would silently reorder keys, causing false
  // diffs in the diff viewer even when nothing meaningful changed.
  const clone = { ...editableUiConfig } as SaveReadyEditablePartOfUiConfig

  if (clone.display_name === null) {
    delete (clone as Partial<typeof clone>).display_name
  }

  if (clone.alias === undefined) {
    clone.alias = {}
  }

  if (clone.dataset_instructions === undefined) {
    clone.dataset_instructions = []
  }

  return clone
}
